import React, { useState, useEffect, useRef } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import "./gamerscluc.css";
import "./Banner.css";
import { Link, useNavigate } from "react-router-dom";
import {
  getGamerClubProducts,
  getPromoEligibility,
  getActivePromos,
  getUserDetails,
} from "../Services/Service";
import { ChevronDown } from "lucide-react";
import chakraLogo from "./FRCLogo.png";
import LazyImage from "../components/LazyImage";
import SpiceGoldModal from "../components/SpiceGoldModal";
import PromoPopup from "../components/PromoPopup";
import { BottomSheetCustom } from "@cred/neopop-web/lib/components";
import BottomLogin from "./bottomLogin";
import { toast, Toaster } from "react-hot-toast";

const CRITICAL_BANNER_URL =
  "https://appcdn.goqii.com/storeimg/44640_1760004379.png";

const GamersClub = () => {
  const [serviceData, setServiceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggleViewMode] = useState(
    sessionStorage.getItem("view") === "true" ? true : false
  );
  const [showPointsModal, setShowPointsModal] = useState(false); // State for the new popup
  const [isPromoEligible, setIsPromoEligible] = useState(false);
  const [activePromos, setActivePromos] = useState([]);
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginSheet, setShowLoginSheet] = useState(false);
  const [shouldAutoClaim, setShouldAutoClaim] = useState(false);
  const [showRegistrationOverlay, setShowRegistrationOverlay] = useState(true);
  const [showLoginOverlay, setShowLoginOverlay] = useState(true);
  const prevRegistrationCondition = useRef(false);
  const prevLoginCondition = useRef(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(
    localStorage.getItem("alreadyRegistered") || "N"
  );
  const navigate = useNavigate();
  // Function to add a preload link to the document head
  const addPreloadLink = (href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    document.head.appendChild(link);
  };

  const getFirstRewardImage = (item) => {
    if (!item?.product?.productImage) return null;
    const { productImage } = item.product;
    try {
      const parsed = JSON.parse(productImage);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (err) {
      return productImage;
    }
  };

  // Check login status and registration status
  useEffect(() => {
    const checkLoginStatus = () => {
      const auth = localStorage.getItem("auth");
      setIsLoggedIn(!!auth);
      setAlreadyRegistered(localStorage.getItem("alreadyRegistered") || "N");
    };

    checkLoginStatus();

    // Listen for storage changes (when user logs in / registers)
    const handleStorageChange = () => {
      checkLoginStatus();
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check periodically (for same-tab login)
    const interval = setInterval(checkLoginStatus, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const user = localStorage.getItem("auth");
    getUserDetails(user)
      .then((res) => {
        // updateInfo();
        if (res.data.data.firstName && res.data.data.lastName) {
          toast.success(`Logged In as ${res.data.data.firstName}!`);
        }
      })
      .catch((err) => console.log(err.message));
  }, []);

  // Auto-close login sheet & prep auto-claim when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      if (showLoginSheet) {
        setShowLoginSheet(false);
      }
      if (isPromoEligible && activePromos.length > 0) {
        setShouldAutoClaim(true);
      }
    } else {
      setShouldAutoClaim(false);
    }
  }, [isLoggedIn, showLoginSheet, isPromoEligible, activePromos]);

  useEffect(() => {
    // 1. Preload the critical banner image
    addPreloadLink(CRITICAL_BANNER_URL);

    // 2. Fetch data
    const fetchData = async () => {
      try {
        const response = await getGamerClubProducts();
        setServiceData(response.data.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Call promo APIs on mount
  useEffect(() => {
    const fetchPromoEligibility = async () => {
      try {
        const response = await getPromoEligibility(1);
        console.log("Promo eligibility response:", response.data);
        const { code, data } = response.data || {};
        if (code === 200 && data && data.eligible === true) {
          setIsPromoEligible(true);
        } else {
          setIsPromoEligible(false);
        }
      } catch (err) {
        console.error("Error fetching promo eligibility:", err);
        setIsPromoEligible(false);
      }
    };

    const fetchActivePromos = async () => {
      try {
        const response = await getActivePromos();
        console.log("Active promos response:", response.data);
        if (response.data && response.data.code === 200 && response.data.data) {
          setActivePromos(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching active promos:", err);
      }
    };

    fetchPromoEligibility();
    fetchActivePromos();
  }, []);

  // Show popup only when eligible and there are active promos
  useEffect(() => {
    if (isPromoEligible && activePromos.length > 0 && isLoggedIn) {
      setShowPromoPopup(true);
    }
  }, [isPromoEligible, activePromos, isLoggedIn]);

  const shouldShowRegistrationOverlay =
    !isLoggedIn && alreadyRegistered === "N" && activePromos.length > 0;
  const shouldShowLoginOverlay =
    !isLoggedIn && alreadyRegistered === "Y" && activePromos.length > 0;

  useEffect(() => {
    if (shouldShowRegistrationOverlay) {
      if (!prevRegistrationCondition.current) {
        setShowRegistrationOverlay(true);
      }
    } else {
      setShowRegistrationOverlay(false);
    }
    prevRegistrationCondition.current = shouldShowRegistrationOverlay;
  }, [shouldShowRegistrationOverlay]);

  useEffect(() => {
    if (shouldShowLoginOverlay) {
      if (!prevLoginCondition.current) {
        setShowLoginOverlay(true);
      }
    } else {
      setShowLoginOverlay(false);
    }
    prevLoginCondition.current = shouldShowLoginOverlay;
  }, [shouldShowLoginOverlay]);

  const alreadyRegisteredFlag =
    localStorage.getItem("alreadyRegistered") || "N";
  const renderRewardsList = (titleText = "Rewards waiting for you…") => {
    if (activePromos.length === 0) return null;

    return (
      <div className="login-overlay-rewards">
        <div className="login-overlay-rewards-title">{titleText}</div>
        <ul>
          {activePromos.flatMap((promo, promoIndex) =>
            (promo.items || []).map((item, itemIndex) => {
              const key = `${promo.id || promoIndex}-${item.id || itemIndex}`;
              const rewardImage =
                item.itemType === "product" ? getFirstRewardImage(item) : null;
              const isPoints = item.itemType === "points";

              const mediaElement = rewardImage ? (
                <LazyImage
                  src={rewardImage}
                  alt={item.product?.productTitle || "Reward"}
                  className="login-overlay-reward-thumb"
                />
              ) : (
                <div className="login-overlay-reward-thumb reward-thumb-icon">
                  <img
                    src={"/assets/images/spicegold.png"}
                    alt="SpiceGold Icon"
                  />
                </div>
              );

              return (
                <li key={key}>
                  {mediaElement}
                  <div className="reward-details">
                    <div className="reward-title" style={{ textAlign: "left" }}>
                      {isPoints
                        ? `${item.quantity} SpiceGold`
                        : item.product?.productTitle || item.itemType}
                    </div>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  console.log(serviceData, "serviceData");

  return (
    <div className="container py-5 mt-2">
      <Toaster position="bottom-center" toastOptions={{ duration: 5000 }} />
      <HelmetProvider>
        <Helmet>
          <title>Rewards Chakra</title>
        </Helmet>
      </HelmetProvider>

      <div className="rewards-chakra-container text-center py-3">
        {/* Logo */}
        <div className="d-flex justify-content-center mb-3 align-items-center mt-2 gap-2">
          {/* REPLACED <img> with LazyImage */}
          <LazyImage
            src={chakraLogo}
            alt="FAU-G Reward Chakra"
            className="chakra-logo img-fluid"
            // Removed loading="lazy" - LazyImage handles it
          />
          {/* Title */}
          <h3 className="rewards-title fw-bold text-uppercase ">
            Rewards Chakra
          </h3>
        </div>

        {/* Banner */}
        <div className="banner-container ">
          {/* REPLACED <img> with LazyImage */}
          <LazyImage
            src={CRITICAL_BANNER_URL} // Use the defined constant
            alt="Promotional Banner"
            className="banner-image img-fluid rounded-4 shadow-sm"
            // Removed decoding="async" and fetchpriority="high" - LazyImage handles optimization
          />
        </div>
      </div>

      {/* Top Info/Button to scroll to "How to Earn Points" */}
      <div className="d-flex justify-content-center">
        <div
          className="how-to-earn-btn d-flex align-items-center justify-content-center"
          // onClick={scrollToHowToEarn}
          onClick={() => setShowPointsModal(true)}
        >
          <span className="fw-semibold me-2">How to Earn SpiceGold</span>
          <ChevronDown size={18} color="#FFD700" />
        </div>
      </div>
      <div className="d-flex justify-content-center mt-3">
        <div
          className="how-to-earn-btn d-flex align-items-center justify-content-center"
          // onClick={scrollToHowToEarn}
          onClick={() => navigate("/refer", { replace: true })}
        >
          <span className="fw-semibold me-2">Earn SpiceGold via Referral</span>
          {/* <ChevronDown size={18} color="#FFD700" /> */}
        </div>
      </div>
      {/* Divider */}
      <div className="d-flex justify-content-center">
        <hr className="divider-line" />
      </div>
      <div className="product-list mb-5">
        {serviceData && serviceData.length > 0 ? (
          <div className="row">
            {serviceData?.map((item, i) => (
              <div
                className={`mb-4 ${
                  !toggleViewMode ? "col-12" : "col-6 col-md-4 col-lg-3 "
                }`}
                key={item.productId}
              >
                <Link to={`/chakra/productdetails/${item.productId}`}>
                  <div className="card h-100 p-1">
                    <div
                      className=" row align-items-center px-2"
                      style={{ marginLeft: !toggleViewMode ? "0" : "-14px" }}
                    >
                      <div
                        className={`${
                          !toggleViewMode
                            ? "col-5 col-lg-2 p-0 ps-md-2"
                            : "col-12"
                        }`}
                      >
                        <div className="card-image ">
                          {/* REPLACED <img> with LazyImage */}
                          <LazyImage
                            className="img-fluid rounded"
                            src={item.productImage}
                            alt={item.productTitle} // Added alt text
                            // Removed loading="lazy" - LazyImage handles it
                          />
                        </div>
                      </div>
                      <div
                        className={`${
                          !toggleViewMode ? "col-7 col-lg-10 p-0" : "col-12"
                        }`}
                      >
                        <div className="card-body px-2 py-2">
                          <h1 className="product-name">{item.productTitle}</h1>
                          <p className="flash-sale m-0">
                            {/* Discount / Flash Sale Placeholder */}
                          </p>
                          <p className="text-success m-0 ">{item.discount}</p>
                          {item.productId !==
                            "VW9TUlFaNU9nUlRvTTV3LzNnbSs1Zz09" && (
                            <div>
                              MRP :
                              <span
                                className="text-light"
                                style={{
                                  textDecoration: "line-through",
                                  marginBottom: "1px !important",
                                }}
                              >
                                {" "}
                                ₹ {item.newPrice}
                              </span>{" "}
                            </div>
                          )}

                          {console.log(item, "gjhgf")}

                          <div
                            className={`product-price-off font-weight-bold m-0 d-flex align-items-center flex-wrap`}
                          >
                            {/* Base Text: Redeem / Pay */}
                            <span
                              className="me-1 text-white-50"
                              style={{ fontSize: "0.9rem" }}
                            >
                              {!toggleViewMode ? "Redeem :" : "Pay:"}{" "}
                            </span>

                            {item.productPrice !== 0 ? (
                              <>
                                {/* --- NON-SPECIAL PRODUCT DISPLAY (e.g., London Trip, iPhone) --- */}
                                {/* Removed redundant d-flex container; flex-wrap on the parent handles it. */}
                                <div className="d-flex align-items-center text-red">
                                  <span
                                    className="fw-bold mb-0 d-flex align-items-center"
                                    style={{
                                      color: "red",
                                      // fontSize: "1.1rem",
                                    }} // Consistent, slightly larger size for price
                                  >
                                    <img
                                      src={"/assets/images/spicegold.png"}
                                      alt="SpiceGold Icon"
                                      className="spice-icon me-1"
                                      style={{
                                        width: "20px", // Slightly smaller icon
                                        height: "20px",
                                      }}
                                    />
                                    <span
                                      style={{
                                        // color: "red",
                                        textDecoration:
                                          item.showPrice !== item.productPrice
                                            ? "line-through"
                                            : "none",
                                        color:
                                          item.showPrice !== item.productPrice
                                            ? "gray"
                                            : "red",
                                      }}
                                    >
                                      {item.showPrice}
                                    </span>{" "}
                                  </span>
                                </div>
                              </>
                            ) : (
                              /* Price is 0 logic (Using LazyImage) */
                              <span className="t-red">
                                <span
                                  style={{
                                    fontWeight: "normal",
                                    color: "white",
                                  }}
                                >
                                  ₹ {item.offerPrice} +
                                </span>{" "}
                                <LazyImage
                                  src={"/assets/images/spicegold.png"}
                                  alt="SpiceGold Icon"
                                  style={{
                                    height: "20px",
                                    width: "20px",
                                    marginBottom: "2.5px",
                                  }}
                                />{" "}
                                {0}
                              </span>
                            )}
                          </div>
                          {item.showPrice !== item.productPrice && (
                            <p
                              className="mb-0  d-flex align-items-center fw-bold blinking-offer"
                              style={{ fontSize: "1rem" }}
                            >
                              {" "}
                              <span
                                className="d-flex fw-bold align-items-center"
                                style={{ color: "red" }}
                              >
                                <img
                                  src={"/assets/images/spicegold.png"}
                                  alt="SpiceGold Icon"
                                  // className="me-1 ms-1"
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                  }}
                                />
                                {item.productPrice}
                              </span>{" "}
                              &nbsp;&nbsp;{" "}
                              <small
                                // className="me-1"
                                style={{ color: "#ffd900ff" }}
                              >
                                Special Deal!
                              </small>{" "}
                              &nbsp;
                            </p>
                          )}
                          <p
                            className="mt-1 mb-0   "
                            style={{
                              color: "#FFD700",
                              fontSize: "0.6em",
                              animation: "pulseGlow 2s infinite ease-in-out",
                            }}
                          >
                            {item.productStock} / {item.totalStock} Left in
                            stock
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-light my-5">
            <h3 className="mb-1">No Products found in Reward.</h3>
            <p>Please check back later for more exciting rewards!</p>
          </div>
        )}

        {/* <div
          ref={howToEarnRef}
          className="container my-3 p-3 rounded-5 shadow text-center"
          style={{
            background: "linear-gradient(45deg, #ccb808ff, #ccb808ff)",
            color: "white",
            cursor: "pointer",
          }}
          onClick={() => setShowPointsModal(true)}
        >
          <h4 className="mb-0 fw-bold">How to Earn Points</h4>
        </div> */}
        {/* Go Up button (Inline) */}
        {/* <div
          className="container my-1 p-2 rounded-pill shadow d-flex align-items-center justify-content-center"
          style={{
            color: "#FFD700",
            cursor: "pointer",
            border: "2px solid #FFD700",
            maxWidth: "300px",
            margin: "auto",
          }}
          onClick={scrollToTop}
        >
          <h4 className="mb-0 fw-bold me-2" style={{ color: "#FFD700" }}>
            Go Up
          </h4>
          <ChevronUp size={20} color="#FFD700" />
        </div> */}
      </div>

      <SpiceGoldModal
        show={showPointsModal}
        onHide={() => setShowPointsModal(false)}
      />

      {/* Promo Popup */}
      {/* <PromoPopup
        show={showPromoPopup}
        onHide={() => setShowPromoPopup(false)}
        promoData={activePromos}
        autoClaim={shouldAutoClaim}
        onAutoClaimComplete={() => setShouldAutoClaim(false)}
      /> */}

      {/* Registration Overlay */}
      {shouldShowRegistrationOverlay && showRegistrationOverlay && (
        <div className="login-overlay">
          <div className="login-overlay-content login-overlay-card registration-overlay-card">
            <h4 className="login-overlay-title">
              {" "}
              <p className="fw-bold fs-3 mb-0 text-warning">
                🎁 Limited Time Offer!
              </p>
            </h4>
            <div className="claim-note mt-1">
              {alreadyRegisteredFlag === "N" ? (
                <>
                  <p className="mt-1 mb-0">
                    Register now and claim your rewards
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-1 mb-0">Log in and claim your rewards:</p>
                </>
              )}

              {renderRewardsList("Registration rewards")}

              <div className="registration-guidelines mt-3">
                <div
                  className="registration-guidelines__item"
                  style={{ textAlign: "left" }}
                >
                  {/* <div className="registration-guidelines__icon">🆕</div> */}
                  <p className="mb-0">
                    🆕 <strong>New users:</strong> Register with OTP and
                    complete your profile to receive rewards.
                  </p>
                </div>
                <div
                  className="registration-guidelines__item"
                  style={{ textAlign: "left" }}
                >
                  {/* <div className="registration-guidelines__icon">🔐</div> */}
                  <p className="mb-0">
                    🔐 <strong>Existing users:</strong> Log in to get your
                    rewards.
                  </p>
                </div>
                <hr className="registration-guidelines__divider" />
                <div className="registration-guidelines__note">
                  {/* <span className="registration-guidelines__note-icon">
                    
                  </span> */}
                  <div>
                    <p style={{ textAlign: "left" }}>Please Note:</p>
                    <p style={{ textAlign: "left" }}>
                      If you do not receive your rewards, tap “Claim Rewards” on
                      the Home Page..
                    </p>

                    <p style={{ textAlign: "left" }}>
                      FAU-G Gold will appear after you exit and restart the
                      game.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="overlay-actions">
              <button
                className="login-overlay-button"
                onClick={() => setShowLoginSheet(true)}
              >
                Get Registration Rewards
              </button>
              <button
                type="button"
                className="overlay-dismiss-btn"
                onClick={() => setShowRegistrationOverlay(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Overlay - shown when user is not logged in */}
      {shouldShowLoginOverlay && showLoginOverlay && (
        <div className="login-overlay">
          <div className="login-overlay-content login-overlay-card">
            <h4 className="login-overlay-title">Login Required</h4>
            <p className="login-overlay-text">
              {isPromoEligible
                ? "Please login to explore Chakra products and get your login rewards."
                : "Please login to explore more."}
            </p>

            {isPromoEligible ? (
              renderRewardsList("Rewards waiting for you…")
            ) : (
              <p className="login-overlay-text mt-3">
                Login to explore more rewards.
              </p>
            )}

            <div className="overlay-actions">
              <button
                className="login-overlay-button"
                onClick={() => setShowLoginSheet(true)}
              >
                Login
              </button>
              <button
                type="button"
                className="overlay-dismiss-btn"
                onClick={() => setShowLoginOverlay(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Login Sheet */}
      <BottomSheetCustom
        open={showLoginSheet}
        handleClose={() => {
          // Allow closing - overlay will remain visible if not logged in
          setShowLoginSheet(false);
        }}
        maxHeight="700px"
        style={{ zIndex: 1200 }}
        sheetStyle={{ zIndex: 1300 }}
      >
        <BottomLogin onClose={() => setShowLoginSheet(false)} />
      </BottomSheetCustom>
    </div>
  );
};

export default GamersClub;
